import React from 'react';
import { useAuth } from '../context/useAuth';
import { useSessionInactivity } from '../hooks/useSessionInactivity';

/**
 * Compact session expiry toast banner.
 * - Shows 30s before logout
 * - Any mouse/click/key auto-extends — no button click needed
 * - Small fixed banner at bottom-right, not a blocking modal
 */
const SessionExpiryModal = () => {
    const { token, logout } = useAuth();
    const { isWarningOpen, secondsLeft, isExtending, extendSession } = useSessionInactivity(30);

    if (!token || !isWarningOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                bottom: '24px',
                right: '24px',
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                backgroundColor: '#1e293b',
                color: '#f1f5f9',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                fontSize: '13px',
                maxWidth: '340px',
                animation: 'slideInUp 0.2s ease-out',
                border: '1px solid rgba(251,191,36,0.3)',
            }}
        >
            {/* Countdown circle */}
            <div style={{ position: 'relative', width: '36px', height: '36px', flexShrink: 0 }}>
                <svg width="36" height="36" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="18" cy="18" r="14" fill="none" stroke="#334155" strokeWidth="3" />
                    <circle
                        cx="18" cy="18" r="14"
                        fill="none"
                        stroke={secondsLeft <= 10 ? '#ef4444' : '#f59e0b'}
                        strokeWidth="3"
                        strokeDasharray={`${2 * Math.PI * 14}`}
                        strokeDashoffset={`${2 * Math.PI * 14 * (1 - secondsLeft / 30)}`}
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.3s' }}
                    />
                </svg>
                <span style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontWeight: 700, fontSize: '11px',
                    color: secondsLeft <= 10 ? '#ef4444' : '#f59e0b'
                }}>
                    {secondsLeft}
                </span>
            </div>

            {/* Message */}
            <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, marginBottom: '2px', color: '#f8fafc' }}>
                    Session expiring
                </div>
                <div style={{ color: '#94a3b8', fontSize: '12px' }}>
                    {isExtending ? 'Refreshing…' : 'Move mouse or click to stay'}
                </div>
            </div>

            {/* Logout button */}
            <button
                onClick={() => logout()}
                style={{
                    background: 'transparent',
                    border: '1px solid #475569',
                    color: '#94a3b8',
                    borderRadius: '6px',
                    padding: '4px 10px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                }}
            >
                Log out
            </button>

            <style>{`
                @keyframes slideInUp {
                    from { opacity: 0; transform: translateY(12px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default SessionExpiryModal;
